import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { BubbleRenderer as InsertionOrderReportBubbleRender } from '@bingads-webui-campaign-react/reports-v4-insertion-order-teaching-bubble-config';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useConstructor } from '@bingads-webui-campaign/hooks';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { ErrorHandlePipeFactory } from '@bingads-webui-campaign/react-hook-use-fetch';
import { errorCodeToErrorMessageMapping, useReportDataSource } from '@bingads-webui-campaign/reports-v4-data-source';
import { useReportsHistory } from '@bingads-webui-campaign/reports-v4-history';
import { useCtrLogger } from '@bingads-webui-campaign/reports-v4-telemetry';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ProgressiveLoadingBlock } from '@bingads-webui-react/ui-next-placeholder';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withReportsV4SiteMap, withCustomReportsSiteMap } from '@bingads-webui-react/with-site-map';
import { getDFMCache, withDFMCache } from '@bingads-webui/dfm-cache';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { DefaultButton, Icon } from '@fluentui/react';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';

import { convertUserIdToScopeSelector, shouldShowUserScopeInput } from '../helpers/account-selector-helper';
import { extractGridItemsFromReportDataResponseBody, hasDifferentTimeColumns } from '../helpers/grid-helper';
import { isInsertionOrderReportTeachingBubbleEnabled } from '../helpers/insertion-order-helper';
import { getStyles } from '../style';
import { TimeTravel } from './TimeTravel';
import { toApi } from './converters';
import { collapsedRowApiFormat } from './converters/convert-collapsed-rows';
import { emptySchedule } from './converters/default-value';
import { ErrorView } from './error-view';
import { PreviewPageImplForCommon } from './preview-page-for-common';
import { PreviewPageImplForUserScopeFilterLevel } from './preview-page-for-user-scope';
import { ReduceScopeView } from './reduce-scope';
import { getDatePickerOptions, shouldShowAccountSelect } from './reduce-scope/helper';
import { getUrlParam, useReportSchemaReportParam } from './utils';


export function ReportPreviewBase({
  siteMap, perfMarker,
}) {
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext);
  const history = useReportsHistory();
  const { reportAction, reportId, reportType } = getUrlParam();
  const perfLoggerSuffix = reportId ? 'Edit' : `Default_${reportType}`;
  const [previewReportPerfMarker] = useConstructor(() => {
    const marker = perfMarker.createChild(`Preview_Report_${perfLoggerSuffix}`);
    marker.willInit();
    return marker;
  });

  const {
    taskState, reportSchema, reportParam, setReportParam: setReportParamImpl, reportModalParam, setReportModalParam,
  } = useReportSchemaReportParam({
    siteMap, i18n, reportAction, reportId, reportType,
  });

  const {
    previewReport, saveReport, saveAsNewReport, downloadReport, refreshRecentReportsDataSource,
  } = useReportDataSource();

  const classes = useDefaultStyles(getStyles);

  const serverContext = useContext(ServerContext);
  const permissions = useContext(PermissionsContext);

  const [reportModalDataItems, setReportModalDataItems] = useState(undefined);
  const [modalTotalCount, setModalTotalCount] = useState(0);
  const [categoryGroup, setCategoryGroup] = useState([]);
  const [parentIndex, setParentIndex] = useState(undefined);

  const [reportDataItems, setReportDataItems] = useState(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [placeHolderError, setPlaceHolderError] = useState(undefined);

  const [timeTravelHistory, setTimeTravelHistory] = useState(new TimeTravel());
  const [isHistoryInitialized, setIsHistoryInitialized] = useState(false);
  const [showReduceScopeBanner, setShowReduceScopeBanner] = useState(false);
  const [showReduceScopePage, setShowReduceScopePage] = useState(false);

  // `savedReportParam` is used to implement "Reset"
  const [savedReportParam, setSavedReportParam] = useState(undefined);
  const previewCallRef = useRef();

  const [dataRefreshId, setDataRefreshId] = useState(0);
  const forceRefreshData = () => {
    setDataRefreshId(dataRefreshId + 1);
  };

  const actionPerfMarker = useRef();
  const ctrLogger = useCtrLogger('ReportsV4PreviewReport');

  const isCreatePage = reportAction === 'create';

  function setReportParam(newReportParam) {
    // store old state
    if (!isHistoryInitialized) {
      timeTravelHistory.pushState(reportParam);
      setIsHistoryInitialized(true);
      setSavedReportParam(reportParam);
    }
    const finalParams = _.extend({}, newReportParam);
    if (reportParam !== newReportParam) {
      // reset pagination if some params change
      const resetPaginationConditions = {
        timeZone: (a, b) => a !== b,
        dateRange: (a, b) => a !== b,
        scopeSelector: (a, b) => a !== b,
        filters: (a, b) => a !== b,
        sortedColumn: (a, b) => a !== b,
        columns: hasDifferentTimeColumns,
      };

      Object.keys(resetPaginationConditions).some((condition) => {
        if (resetPaginationConditions[condition](reportParam[condition], newReportParam[condition], reportSchema) && !_.get(newReportParam, 'skipResetPagination', false)) {
          finalParams.GridPagination = { RequestedPage: 1, RowsPerPage: reportParam.GridPagination.RowsPerPage };
          return true;
        }
        return false;
      });

      // reset sort if current sorted column is removed. If new sorted column can be found in new columns, use it.
      if (reportParam.columns !== newReportParam.columns && !newReportParam.columns.includes(newReportParam.sortedColumn.key) && !newReportParam.columns.includes(reportParam.sortedColumn.key)) {
        finalParams.sortedColumn = {};
      }
    }
    timeTravelHistory.pushState(finalParams);
    setReportParamImpl(finalParams);
  }

  // when user chooses another report, reset time travel history
  useEffect(() => {
    setIsHistoryInitialized(false);
    setTimeTravelHistory(new TimeTravel());
    setSavedReportParam();
  }, [reportAction, reportId, reportType]);

  // enable time travel history callback
  useEffect(
    () => {
      const timeTravelCallback = (storedReportParamState) => {
        // restore stored state
        setReportParamImpl(storedReportParamState);
      };
      timeTravelHistory.registerTimeTravelCallback(timeTravelCallback);
      return () => {
        timeTravelHistory.unregisterTimeTravelCallback(timeTravelCallback);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [timeTravelHistory, setReportParamImpl]
  );

  const notifyErrorPipe = ErrorHandlePipeFactory.createNotifyErrorHandlerPipe(i18n, errorCodeToErrorMessageMapping);

  async function onSave(newReportParam) {
    const reportParamToSave = newReportParam || reportParam;
    const patchParm = toApi(reportParamToSave, reportSchema).ReportData;
    try {
      await saveReport(patchParm);
      setSavedReportParam(reportParamToSave);
      notificationApi.showInfo(i18n.getString(_TL_('Report saved.')), {
        autoDismissTime: 5000,
      });
    } catch (e) {
      notifyErrorPipe(e);
      throw e;
    }
  }

  async function onSaveAs(newReportParam) {
    try {
      const { Id: newTaskItemId } = await saveAsNewReport(toApi(newReportParam, reportSchema));
      history.push(`/campaign/vnext/reports-v4/edit/${newTaskItemId}`);
    } catch (e) {
      notifyErrorPipe(e);
      throw e;
    }
  }

  async function onDownload(format, isDirty) {
    const { aid } = history.query;
    const { CurrentCustomer, CurrentAccount } = serverContext;
    const customerOrAccountName = aid ? CurrentAccount.Name : CurrentCustomer.Name;
    const reportName = (!isDirty && isCreatePage) ?
      `${reportParam.reportName} ${i18n.formatDate(new Date(), { timeZone: null, skeleton: 'yMd' })} ${customerOrAccountName}`
      : reportParam.reportName;
    const payload = toApi({
      ...reportParam,
      reportName,
      Format: format,
      Type: 'ReportUI', // One time report, do not show in saved reports
      ReportOptions: _.union(reportParam.ReportOptions, ['IncludeTotalRows']),
      schedule: emptySchedule, // fix bug 4178997: can not download with a Cron
    }, reportSchema);
    notificationApi.showInfo(i18n.getString(_TL_('We are creating your report and it will be downloaded shortly.')), {
      autoDismissTime: 5000,
    });
    try {
      await downloadReport(payload);
    } catch (error) {
      notifyErrorPipe(error);
    }
  }

  const [isUserScopeFilterLevelPreviewPageInited, setIsUserScopeFilterLevelPreviewPageInited] = useState(false);
  const isUserScope = reportSchema && shouldShowUserScopeInput(reportSchema.ScopeFilterLevel);
  const shouldUserScopeFilterLevel = isCreatePage && isUserScope && !isUserScopeFilterLevelPreviewPageInited;

  function pickPreviewPageImpl() {
    function onApplyClick(newUserId) {
      const newScopeSelector = convertUserIdToScopeSelector(newUserId);
      setReportParam({ ...reportParam, scopeSelector: newScopeSelector });
      setIsUserScopeFilterLevelPreviewPageInited(true);
    }

    if (shouldUserScopeFilterLevel) {
      return (<PreviewPageImplForUserScopeFilterLevel
        onApplyClick={onApplyClick}
        reportParam={reportParam}
      />);
    }

    // In edit mode, when task item is still loading
    if (!reportSchema || !reportParam) {
      return <ProgressiveLoadingBlock />;
    }

    return (<PreviewPageImplForCommon
      // !!!important: Do not remove `key` prop, otherwise impression log is not accurate.
      key={`${reportAction}-${reportType}-${reportId}`}
      classes={classes}
      reportAction={reportAction}
      reportId={reportId}
      reportSchema={reportSchema}
      reportParam={reportParam}
      setReportParam={setReportParam}
      savedReportParam={savedReportParam}
      reportDataItems={reportDataItems}
      totalCount={totalCount}
      reportModalParam={reportModalParam}
      setReportModalParam={setReportModalParam}
      reportModalDataItems={reportModalDataItems}
      modalTotalCount={modalTotalCount}
      onSave={onSave}
      onSaveAs={onSaveAs}
      onDownload={onDownload}
      timeTravelHistory={timeTravelHistory}
      perfMarker={previewReportPerfMarker}
      actionPerfMarker={actionPerfMarker.current}
      ctrLogger={ctrLogger}
      placeHolderError={placeHolderError}
      forceRefreshData={forceRefreshData}
      categoryGroup={categoryGroup}
      setCategoryGroup={setCategoryGroup}
      parentIndex={parentIndex}
    />);
  }

  const shouldNotFetch = shouldUserScopeFilterLevel;
  const hasReportGridModal = serverContext && serverContext.Permissions && serverContext.Permissions.dynamic &&
    serverContext.Permissions.dynamic.CollapsedRow && reportSchema && reportSchema.ReportType === 'PublisherUsagePerformanceReport';
  const hasSearchInsightReportGridModal = permissions && permissions.dynamic && permissions.dynamic.SearchInsightPerformanceReport && reportSchema && (reportSchema.ReportType === 'SearchInsightPerformanceReport');

  useEffect(() => {
    let isMounted = true;
    async function fetchAndUpdateData() {
      if (reportDataItems) {
        actionPerfMarker.current = perfMarker.createChild(`Preview_Report_${perfLoggerSuffix}_Action`);
      }
      setReportDataItems(undefined); // show shimmer when loading the state
      setPlaceHolderError(undefined);
      setReportModalDataItems(undefined);
      setCategoryGroup([]);
      setParentIndex(undefined);
      if (reportParam) {
        previewReportPerfMarker.willFetchData();
        if (actionPerfMarker.current) {
          actionPerfMarker.current.willFetchData();
        }

        const payload = toApi({
          ...reportParam,
          ...(hasReportGridModal && { CollapsedRow: collapsedRowApiFormat(false) }),
          ReportOptions: _.union(reportParam.ReportOptions, ['IncludeTotalRows']),
        }, reportSchema, { hasPagination: true });
        let reduceScopeTimer = null;
        try {
          reduceScopeTimer = setTimeout(() => {
            const datePickerOptions = getDatePickerOptions({ reportParam, reportSchema, i18n });
            const shouldShowAccountSelector = shouldShowAccountSelect({ reportSchema, scope });
            if (datePickerOptions.length > 1 && shouldShowAccountSelector) {
              setShowReduceScopeBanner(true);
              ctrLogger.click({ target: 'ReduceScopeBannerShow', extraData: { reportParam } });
            }
            setTimeout(() => {
              setShowReduceScopeBanner(false);
            }, 60 * 1000);
          }, 60 * 1000);

          let previewCall;
          let dfmCacheResult;
          if (dataRefreshId === 0) { // first load
            const dfmCacheKey = `getRecentReports${reportAction}${reportId || reportType}DFM`;
            dfmCacheResult = getDFMCache(dfmCacheKey);
            previewCall = withDFMCache(_.partial(previewReport, payload), dfmCacheKey, this);
          } else {
            previewCall = _.partial(previewReport, payload);
          }
          previewCallRef.current = previewCall;
          const responseBody = await previewCall();

          clearTimeout(reduceScopeTimer);
          setShowReduceScopeBanner(false);
          if (!responseBody.isException) {
            const newItems = extractGridItemsFromReportDataResponseBody(responseBody, i18n, reportParam, serverContext.Permissions, ctrLogger);
            if (isMounted) {
              previewReportPerfMarker.willRender();
              if (actionPerfMarker.current) {
                actionPerfMarker.current.willRender();
              }
              setTotalCount(responseBody['@odata.count']);
              setReportDataItems(newItems);
            }
          } else {
            setTotalCount(0);
            setReportDataItems([]);
          }
          if (dfmCacheResult) {
            refreshRecentReportsDataSource();
          }
        } catch (e) {
          // the request might be send by JQuery(by DFM) or fetch, when it's jquery, read the JSON response body.
          const errorResponseBody = e.responseJSON ? e.responseJSON : e;
          setTotalCount(0);
          if (errorResponseBody['@odata.context'] && errorResponseBody['@odata.context'].includes('Model.AdsApiError') && errorResponseBody.value[0].Code === 'DatabaseTimeout') {
            ctrLogger.click({ target: 'DatabaseTimeout', extraData: { reportParam } });
            setPlaceHolderError(_TL_('This report has more data than we can show here. To view the whole report, you can download it.'));
          }
          setReportDataItems([]);
          clearTimeout(reduceScopeTimer);
          notifyErrorPipe(errorResponseBody);
        }
      }
    }
    if (!shouldNotFetch) {
      fetchAndUpdateData();
    }
    return function cleanUp() {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportParam, shouldNotFetch, dataRefreshId]);


  useEffect(() => {
    let isMounted = true;
    async function fetchAndUpdateData() {
      if ((hasReportGridModal && reportModalParam && reportModalParam.CollapsedRow && reportModalParam.CollapsedRow.ExpandCollapsedRows) ||
        (hasSearchInsightReportGridModal && reportModalParam && reportModalParam.SearchCategory)) {
        const payload = toApi({
          ...reportModalParam,
          ReportOptions: _.union(reportModalParam.ReportOptions, ['IncludeTotalRows']),
        }, reportSchema, { hasPagination: true });
        try {
          const previewCall = _.partial(previewReport, payload);
          previewCallRef.current = previewCall;
          const responseBody = await previewCall();

          if (!responseBody.isException) {
            const newItems = extractGridItemsFromReportDataResponseBody(responseBody, i18n, reportParam, serverContext.Permissions, ctrLogger);
            if (isMounted) {
              setModalTotalCount(responseBody['@odata.count']);
              setReportModalDataItems(newItems);
              setParentIndex(reportModalParam.ParentIndex);
            }
          } else {
            setModalTotalCount(0);
            setReportModalDataItems([]);
          }
        } catch (e) {
          // the request might be send by JQuery(by DFM) or fetch, when it's jquery, read the JSON response body.
          const errorResponseBody = e.responseJSON ? e.responseJSON : e;
          setModalTotalCount(0);
          if (errorResponseBody['@odata.context'] && errorResponseBody['@odata.context'].includes('Model.AdsApiError') && errorResponseBody.value[0].Code === 'DatabaseTimeout') {
            ctrLogger.click({ target: 'DatabaseTimeout', extraData: { reportModalParam } });
            setPlaceHolderError(_TL_('This report has more data than we can show here. To view the whole report, you can download it.'));
          }
          setReportModalDataItems([]);
          notifyErrorPipe(errorResponseBody);
        }
      }
    }
    if (!shouldNotFetch) {
      fetchAndUpdateData();
    }
    return function cleanUp() {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportModalParam, shouldNotFetch]);

  const reduceScopeBanner = (
    <div className="reduce-scope-banner">
      <Icon iconName="Error" />
      <span>{i18n.getString(_TL_('Report loading slow. Pause report and change scope?'))}</span>
      <DefaultButton
        text={i18n.getString(_TL_('Pause'))}
        onClick={() => {
          setShowReduceScopePage(true);
          setShowReduceScopeBanner(false);
          if (previewCallRef.current) {
            if (previewCallRef.current.cancel) {
              previewCallRef.current.cancel();
            }
            setTotalCount(0);
            setReportDataItems([]);
          }
          ctrLogger.click({ target: 'ReduceScopeBannerClick', extraData: { reportParam }, parentTarget: 'ReduceScopeBannerShow' });
        }}
      />
    </div>
  );

  const renderNormalView = () => (
    <React.Fragment>
      {taskState === 'success' && pickPreviewPageImpl()}
      {(taskState === 'invalidId' || taskState === 'fail' || taskState === 'deprecate') && <ErrorView taskState={taskState} />}
      {showReduceScopeBanner && reduceScopeBanner}
    </React.Fragment>
  );

  const renderReduceScopeView = () => (
    <ReduceScopeView
      classes={classes}
      reportParam={reportParam || {}}
      reportSchema={reportSchema}
      ctrLogger={ctrLogger}
      onApply={({ dateRange, scopes }) => {
        setReportParam({
          ...reportParam,
          dateRange,
          scopeSelector: scopes,
        });
        setShowReduceScopePage(false);
      }}
      onDismiss={() => {
        setShowReduceScopePage(false);
      }}
    />
  );

  return (
    <div className={`${classes.reportPreviewContainer} ${hasReportGridModal ? classes.reportPreviewPoorCollapseRow : ''} report-preview-container`}>
      {showReduceScopePage ? renderReduceScopeView() : renderNormalView()}
      {isInsertionOrderReportTeachingBubbleEnabled({ reportType, permissions: serverContext.Permissions, isCreatePage }) &&
        <InsertionOrderReportBubbleRender />}
    </div>
  );
}

ReportPreviewBase.propTypes = {
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

const ReportPreviewWrapped = withReportsV4SiteMap(ReportPreviewBase);
const CustomReportPreviewWrapped = withCustomReportsSiteMap(ReportPreviewBase);

export { ReportPreviewWrapped as ReportPreview };
export { CustomReportPreviewWrapped as CustomReportPreview };

